<template>
    <div>
        <progress-bar v-model="percentageCompleted" type="info" striped label />
        <div class="row">
            <div v-if="authenticated" class="col-md-6">
                <treeselect
                    :value="contentType"
                    :options="jsonParsedOptions"
                    :placeholder="'Please select a metadata type'"
                    :multiple="false"
                    :clearable="true"
                    :disable-branch-nodes="true"
                    :show-count="true"
                    :flatten-search-results="true"
                    @input="changeContentType"
                />
            </div>
            <div v-if="authenticated" class="col-md-6">
                <treeselect
                        :options="workgroupOptions"
                        :placeholder="'Please select a Workgroup (optional)'"
                        :multiple="false"
                        :clearable="true"
                        v-model="selectedWorkGroup"
                    />
                    <span class="help-block">
                            If you need to include the <strong><em>Alternative Names</em></strong> or <strong><em>Identifiers</em></strong>, you must select a Workgroup to <em>download</em> the template.
                            Additionally, make sure to select the correct Workgroup before proceeding to <em>upload</em> your file.
                    </span>
            </div>
            <div v-else class="col-md-12">
                <treeselect
                    :value="contentType"
                    :options="jsonParsedOptions"
                    :placeholder="'Please select a metadata type'"
                    :multiple="false"
                    :clearable="true"
                    :disable-branch-nodes="true"
                    :show-count="true"
                    :flatten-search-results="true"
                    @input="changeContentType"
                />
            </div>
        </div>
        <div class="section-title">Download Template</div>
        <div v-if="authenticated && contentType" class="row checkboxGroup">
            <h4 class="col-md-12">Options</h4>
            <div class="col-md-6">
                <h4>Custom Fields <a :href="getHelpLink" target="_blank"><i class="fa fa-question-circle" /></a></h4>
                <div>
                    <input type="checkbox" v-model="includeCommonCustomFields"> Include general custom fields
                </div>
                <div>
                    <input type="checkbox" v-model="includeSpecificCustomFields"> Include
                    <strong>{{ contentType ? optionsMapping[contentType] : 'model' }}</strong> specific custom fields
                </div>
            </div>
            <div class="col-md-6">
                <h4>Alternative Names and Identifiers</h4>
                <div>
                    <input type="checkbox" v-model="includeAlternativeNames" :disabled="!isTreeselectSelected"> Include alternative names
                </div>
                <div>
                    <input type="checkbox" v-model="includeIdentifiers" :disabled="!isTreeselectSelected"> Include identifiers<a :href="namespaceHelpLink" target="_blank"><i v-if="this.selectedWorkGroup" class="fa fa-question-circle" /></a>
                </div>
            </div>
        </div>
        <div class="text-center download-btn">
                    <a
                        class="btn btn-outline-info btn-block"
                        :class="{'disabled': !contentType}"
                        :href="getUrl(computedDownloadUrl)"
                        :download="contentType"
                    >
                        <i v-if="contentType" class="fad fa-file-download" />
                        <span v-else>&ZeroWidthSpace;</span>
                        {{ getDownloadButtonText }}
                    </a>
        </div>
       <div class="section-title">Upload Metadata</div>
        <aristotle-file-pond
            ref="aristotlepond"
            :url="url"
            :status-url="statusUrl"
            :label-idle="getLabelIdle()"
            :current-tags="currentTags"
            :selected-work-group="selectedWorkGroup"
            :content-type="contentType"
            :disabled="!contentType"
            @changeProgress="changeProgress"
            @changeLoadingMessage="changeLoadingMessage"
            @changeNumberOfSteps="changeNumberOfSteps"
            @changeFileIsLoaded="changeFileIsLoaded"
            @changeErrors="changeErrors"
            @changeListOfMetadataRepresentations="changeListOfMetadataRepresentations"
            @changeIsLoading="changeIsLoading"
            @changeExcelToCsvMessage="changeExcelToCsvMessage"
            @changeShowLoginMessage="changeShowLoginMessage"
            @changeTags="changeTags"
            @changeSelectedWorkgroup="changeSelectedWorkgroup"
        />
        <div v-if="contentType">
            <file-uploader-error-display :errors="errors" />
            <div v-if="showLoginMessage" class="alert alert-info">
                Your file has been processed and the following metadata items were found in the file.
                You will need to log in to save these metadata items.
            </div>
            <div v-if="excelToCsvMessage" class="alert alert-info">
                {{ excelToCsvMessage }}
            </div>
            <div class="row">
                <div class="col-sm-4">
                    <file-uploader-options
                        v-if="fileIsLoaded"
                        :user-workgroups="userWorkgroups"
                        :user-tags="userTags"
                        type="abc"
                        @changeTags="changeTags"
                    />
                    <table-of-contents
                        :list-of-metadata-representations="listOfMetadataRepresentations"
                    />
                </div>
                <div class="col-sm-8">
                    <button v-if="fileIsLoaded" class="btn btn-success pull-right" @click="submitFile" :disabled="isDisabled">
                        {{ buttonMessage }}
                    </button>
                    <p v-if="fileIsLoaded">
                        Your file has been processed and the following metadata items were found in the
                        file.<span v-if="isDisabled">When you include the <strong><em>Alternative Names</em></strong> or <strong><em>Identifiers</em></strong>, you must select a Workgroup first, then</span>
                        click "{{ buttonMessage }}" to save these items into the registry.
                    </p>
                    <p v-if="percentageCompleted === 100">
                        The objects provided in the file have been successfully created! Click on the links below to see
                        the items in a new tab.
                    </p>
                    <metadata-representation
                        :list-of-metadata-representations="listOfMetadataRepresentations"
                        :namespace-id-name-mapping-api-url="namespaceIdNameMappingApiUrl"
                        :selected-work-group="selectedWorkGroup"
                        :workgroups="workgroupOptions"
                        :file-is-loaded="fileIsLoaded"
                        :system-name-to-name-mapping="systemNameToNameMapping"
                    />
                    <button v-if="fileIsLoaded" class="btn btn-success pull-right" @click="submitFile" :disabled="isDisabled">
                        {{ buttonMessage }}
                    </button>
                </div>
                <loading v-if="isLoading" :loading-message="loadingMessage" />
            </div>
        </div>
    </div>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import apiRequest from 'src/mixins/api-request.js'
import AristotleFilePond from "@/fileuploader/AristotleFilePond.vue"
import FileUploaderErrorDisplay from '@/fileuploader/FileUploaderErrorDisplay.vue'
import FileUploaderOptions from "@/fileuploader/FileUploaderOptions.vue"
import MetadataRepresentation from "@/fileuploader/MetadataRepresentation.vue"
import TableOfContents from "@/fileuploader/TableOfContents.vue"
import Loading from 'src/components/Loading.vue'
import Constants from "@/fileuploader/constants.js"
import {ProgressBar} from 'uiv'
import aristotleFilePondMixin from "@/fileuploader/aristotle-file-pond-mixin"

export default {
    name: "AbcFileUploader",
    components: {
        Treeselect,
        AristotleFilePond,
        FileUploaderErrorDisplay,
        FileUploaderOptions,
        MetadataRepresentation,
        TableOfContents,
        Loading,
        ProgressBar,
    },
    mixins: [apiRequest, aristotleFilePondMixin],
    props: {
        url: {
            type: String,
            required: true,
        },
        statusUrl: {
            type: String,
            required: true,
        },
        downloadTemplateApiEndpointUrl: {
            type: String,
            required: true,
        },
        namespaceIdNameMappingApiUrl: {
            type: String,
            required: true,
        },
        options: {
            type: String,
            required: true,
        },
        userTags: {
            type: String,
            required: true,
        },
        userWorkgroups: {
            type: String,
            required: true,
        },
        authenticated: {
            type: Boolean,
            required: true
        },
        workgroupStewardSlugMapping: {
            type: Object,
            required: true,
        },
        systemNameToNameMapping: {
            type: Object,
            default: () => ({}),
        }
    },
    data: () => ({
        fileValidateTypeLabelExpectedTypesMap: Constants.fileValidateTypeLabelExpectedTypesMap,
        labelIdle: Constants.labelIdle,
        acceptedFileTypes: Constants.acceptedFileTypes,
        isLoading: false,
        fileIsLoaded: false,
        listOfMetadataRepresentations: [],
        buttonMessage: 'Save metadata',
        loadingMessage: 'Reading file...',
        percentageCompleted: 0,
        jsonParsedOptions: [],
        optionsMapping: {},
        contentType: null,
        currentTags: [],
        selectedWorkGroup: null,
        workgroupOptions: [],
        excelToCsvMessage: '',
        numberOfSteps: 2,
        showLoginMessage: false,
        includeCommonCustomFields: false,
        includeSpecificCustomFields: false,
        helpLinkmapping: {},
        includeAlternativeNames: false,
        includeIdentifiers: false,
    }),
    computed: {
        getDownloadButtonText: function () {
            if (this.contentType) {
                return `Download template file for ${this.optionsMapping[this.contentType]}`
            }
            return ""
        },
        getHelpLink: function () {
            if (this.contentType) {
                return this.helpLinkmapping[this.contentType]
            }
            return "/help/concepts/"
        },
        computedDownloadUrl() {
            let downloadUrl = this.downloadTemplateApiEndpointUrl
            const queryParams = []

            if (this.includeCommonCustomFields) {
                queryParams.push('include_common_cf=true')
            }

            if (this.includeSpecificCustomFields) {
                queryParams.push('include_specific_cf=true')
            }
            const hasWG = this.selectedWorkGroup !== null && this.selectedWorkGroup !== undefined
            const needsWG = hasWG && (
                this.includeAlternativeNames ||
                this.includeIdentifiers ||
                this.includeCommonCustomFields ||
                this.includeSpecificCustomFields
            )

            if (needsWG) {
                queryParams.push(`wg=${encodeURIComponent(this.selectedWorkGroup)}`)
            }

            if (hasWG && this.includeAlternativeNames) {
                queryParams.push('include_alternative_name=true')
            }
            if (hasWG && this.includeIdentifiers) {
                queryParams.push('include_identifier=true')
            }

            if (queryParams.length > 0) {
                downloadUrl += '?' + queryParams.join('&')
            }

            return downloadUrl
        },
        isTreeselectSelected() {
            return this.selectedWorkGroup !== null && this.selectedWorkGroup !== undefined
        },
        isDisabled() {
            if (this.selectedWorkGroup) {
                return false
            }
            for (const representation of this.listOfMetadataRepresentations) {
                if (representation && representation.mapping) {
                    for (const mappingItem of representation.mapping) {
                        if (mappingItem && mappingItem.fields) {
                            const fields = mappingItem.fields
                            const alternativeNames = fields.alternative_names
                            const identifiers = fields.identifiers

                            if ((alternativeNames !== undefined && Object.keys(alternativeNames).length > 0) ||
                                ( identifiers !== undefined && identifiers.length > 0)) {
                                return !this.selectedWorkGroup
                            }
                        }
                    }
                }
            }
            return false
        },
        namespaceHelpLink() {
            if (this.selectedWorkGroup !== null && this.selectedWorkGroup !== undefined) {
                const stewardSlug = this.workgroupStewardSlugMapping[this.selectedWorkGroup]
                return `/steward/${stewardSlug}/managed_items/namespace`
            }
            return null
        },
    },
    watch: {
        selectedWorkGroup: function (newValue) {
            if (!newValue) {
                this.includeAlternativeNames = false
                this.includeIdentifiers = false
            }
        },
    },
    created() {
        this.jsonParsedOptions = JSON.parse(this.options)
        for (let app_representation of this.jsonParsedOptions) {
            for (let child of app_representation.children) {
                this.optionsMapping[child.id] = child.label
                this.helpLinkmapping[child.id] = `/help/concepts/${child.app_label}/${child.model}/#custom_fields`
            }
        }
        const jsonParseWorkgroups = JSON.parse(this.userWorkgroups)
        this.workgroupOptions = this.getWorkgroupOptions(jsonParseWorkgroups)
    },
    methods: {
        changeContentType: function (contentType) {
            this.contentType = contentType
            this.$refs.aristotlepond.removeFile()
        },
        getUrl: function (url) {
            return url.replace('{metadata_content_type_id}', this.contentType)
        },
        getLabelIdle: function () {
            if (this.contentType) {
                return this.labelIdle
            }
            return "Please select a metadata type."
        },
        getWorkgroupOptions: function (wgs) {
            if (typeof wgs === 'object' && !Array.isArray(wgs)) {
                return []
            }
            return wgs.map((obj) => {
                return {
                    id: obj.id,
                    label: obj.name,
                }
            })
        },
    },
}
</script>

<style scoped>
.checkboxGroup {
    margin-bottom: 10px;
    padding-bottom: 10px;
}
.checkboxGroup a {
    font-size: 1.5rem;
}
.section-title {
    font-size: 21px;
    padding-bottom: 5px;
    border-bottom: 1px solid #eeeeee;
    margin-top: 10px;
}
.download-btn {
    margin-bottom: 30px;
}
</style>
